import { RoundCard } from "@/components/homepage/RoundCard";
import { Prize } from "@/components/rounds/Prize";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandleError } from "@/hooks/useHandleError";
import { useSkipPrizesStore } from "@/store";
import { useSoccerRound } from "@roman/shared/hooks/soccer/useSoccerRound";
import { useSoccerRounds } from "@roman/shared/hooks/soccer/useSoccerRounds";
import type { HTMLAttributes } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MatchName } from "./PlayerCard/components/MatchName";

export const Prizes = () => {
  const navigate = useNavigate();
  const { roundId } = useParams();
  const { skipPrizes, setSkipPrizes } = useSkipPrizesStore();

  if (!roundId) {
    return <div>エラーが発生しました</div>;
  }

  const handleError = useHandleError("ラウンドの取得に失敗しました");

  const { data: roundData, isLoading: isRoundLoading } = useSoccerRound(roundId, () => {}, "ja");
  const { data: roundsData, isLoading: isRoundsLoading } = useSoccerRounds("FreePoints", handleError, "ja");

  if (!roundData || isRoundLoading || isRoundsLoading) {
    return <LoadingSkeleton />;
  }
  const round = roundsData.rounds.find((round) => round.id === roundId);
  if (!round) {
    return <div>賞金ラウンドが見つかりません</div>;
  }

  const SkipPrizesCheckbox = () => (
    <>
      <button
        type="button"
        onClick={() => navigate(`/rounds/${round.id}/select-players`)}
        className="bg-gradient-primary text-white font-bold px-4 py-2 rounded-md w-full"
      >
        エントリーする
      </button>
      <div className="my-4 flex justify-center">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="skipPrizes"
            className="w-5 h-5 accent-primary"
            checked={skipPrizes}
            onChange={(e) => setSkipPrizes(e.target.checked)}
          />
          <label htmlFor="skipPrizes" className="text-[10px] sm:text-xs text-dark-gray leading-4 sm:leading-5">
            次回からこの画面はスキップし、選手選択画面からスタートする。
            <br />
            ※賞金は大会情報画面でいつでも確認できます。
          </label>
        </div>
      </div>
    </>
  );

  return (
    <div className="w-full h-full">
      <MatchName roundId={roundId} homeName={round.homeName} awayName={round.awayName} />
      <div className="flex flex-col items-center w-full bg-white bg-cover bg-top">
        <div>
          <RoundCard round={round} onClick={() => {}} showFooter={false} />
          <div className="py-8">
            <SkipPrizesCheckbox />
            <Prize round={round} />
            <SkipPrizesCheckbox />
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadingSkeleton = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="min-h-screen w-full bg-white" {...props}>
      <div className="flex flex-col justify-center items-center py-2 bg-blue px-2 lg:px-6">
        <Skeleton className="w-full h-[40px] bg-white" />
      </div>

      <div className=" px-2 lg:px-6">
        <div>
          <Skeleton className="h-[220px] lg:h-[350px] py-2 w-full mt-2" />
        </div>

        <div className="mt-10">
          <Skeleton className="h-[40px] py-2 w-full" />
        </div>

        <div className="w-full flex justify-center">
          <div className="mt-4 flex gap-1 items-center justify-center w-2/3 px-2">
            <div>
              <Skeleton className="h-[20px] w-[20px] py-2 " />
            </div>
            <div className="w-full flex flex-col gap-1">
              <Skeleton className="h-[20px] py-2 w-full" />
              <Skeleton className="h-[20px] py-2 w-full" />
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full my-4">
          <Skeleton className="w-20 h-[20px] py-2" />

          <div className="flex flex-col gap-2 w-full mt-4">
            <Skeleton className="h-[40px] w-full py-2 " />
            <Skeleton className="h-[40px] w-full py-2 " />
            <Skeleton className="h-[40px] w-full py-2 " />
          </div>
        </div>

        <div className="mt-10">
          <Skeleton className="h-[40px] py-2 w-full" />
        </div>

        <div className="w-full flex justify-center">
          <div className="mt-4 flex gap-1 items-center justify-center w-2/3 px-2">
            <div>
              <Skeleton className="h-[20px] w-[20px] py-2 " />
            </div>
            <div className="w-full flex flex-col gap-1">
              <Skeleton className="h-[20px] py-2 w-full" />
              <Skeleton className="h-[20px] py-2 w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
